import { useEffect, useMemo, useRef } from 'react';
import Button from '~/commonComponents/Button';
import { Column } from '~/commonComponents/Column';
import { Icon } from '~/commonComponents/Icon';
import { Pressable } from '~/commonComponents/Pressable';
import { Row } from '~/commonComponents/Row';
import { Text } from '~/commonComponents/Text';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { getShouldEnforceBestInDental } from '~/growthbook';
import { useToggle } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import BestInDentalBadge from '~/images/best-in-dental.png';
import { Colors } from '~/themes/colors';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { URLUtils } from '~/utils/URLUtils';
import { getBestInDentalStorageKey } from './Constants';

export const BestInDentalNomineePopup = () => {
  const currentUser = useUserSelector();
  const [isOpen, controls] = useToggle(false);

  const isNominee = useMemo(() => {
    return currentUser?.office_info?.bestInDental?.status === 'nominee';
  }, [currentUser?.office_info?.bestInDental?.status]);

  const isFeatureActive = useRef(getShouldEnforceBestInDental()).current;

  useEffect(() => {
    if (!currentUser || !isFeatureActive) return;

    const key = getBestInDentalStorageKey(currentUser.id);

    const result = localStorage.getItem(key);

    if (result === 'true' || !isNominee) {
      controls.setOff();
      return;
    }

    controls.setOn();
  }, [controls, currentUser, isFeatureActive, isNominee]);

  const handleDeclineNomination = () => {
    controls.setOff();
    localStorage.setItem(getBestInDentalStorageKey(currentUser.id), 'true');
  };

  const handleSubmitApplication = () => {
    window.open(
      URLUtils.fromTemplate(RemoteConfigUtils.getBestInDentalOfficeNomineeSurvey(), {
        officeId: currentUser?.id,
        officeName: currentUser?.office_name,
      }),
      '_blank',
    );
    controls.setOff();
    localStorage.setItem(getBestInDentalStorageKey(currentUser.id), 'true');
  };

  if (!isOpen) {
    return null;
  }

  return (
    // @ts-expect-error - ConfirmPopup still written in JavaScript
    <ConfirmPopup
      modalStyle={{ maxWidth: 500, maxHeight: 560, paddingTop: 57 }}
      Icon={() => (
        <img alt="Best In Dental Badge" src={BestInDentalBadge} width={164} height={184} />
      )}
      title="Your Office Has Been Nominated!"
      titleStyle={{ marginBlock: 16 }}
      description={
        <Column>
          <Text color={Colors.neutral_500} marginBottom={12}>
            You&apos;ve been handpicked.
          </Text>
          <Text color={Colors.neutral_500}>
            GoTu leadership has nominated your office for our Best in Dental list, an honor reserved
            for the top 10% of practices setting the standard in patient care, team culture, and
            professional reputation. Dental professionals in your area already notice. Now it&apos;s
            your turn to claim it.
          </Text>
        </Column>
      }
      descriptionStyle={{ marginBottom: 24, width: '85%' }}
    >
      <Pressable onPress={handleSubmitApplication}>
        <Row className="button medium primary" gap={6} width={228}>
          <Icon name="external-link" color={Colors.white} />
          <Text bold color={Colors.white} fontSize={18}>
            Submit Application
          </Text>
        </Row>
      </Pressable>
      <Button
        style={{ backgroundColor: Colors.secondary_500, width: 256 }}
        text="Decline Nomination"
        onClick={handleDeclineNomination}
      />
    </ConfirmPopup>
  );
};
